import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Search, ShieldCheck, KeyRound, Crown } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function AdminPanel({ open, onClose }) {
  const { authFetch } = useAuth();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (query = "") => {
    try {
      const d = await authFetch(`/admin/users?q=${encodeURIComponent(query)}`);
      setUsers(d.users || []);
      setTotal(d.total || 0);
    } catch (e) { toast.error(e.message || "Could not load users"); }
  }, [authFetch]);

  useEffect(() => { if (open) load(""); }, [open, load]);

  const reset = async (u) => {
    const pw = window.prompt(`Set a NEW password for @${u.username} (${u.email}).\nThey'll use it to log in with email. Min 6 characters:`);
    if (pw == null) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setBusyId(u.id);
    try {
      await authFetch(`/admin/users/${u.id}/reset-password`, { method: "POST", body: JSON.stringify({ newPassword: pw }) });
      toast.success(`Password reset for @${u.username}`);
    } catch (e) { toast.error(e.message || "Reset failed"); }
    finally { setBusyId(null); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="admin-panel">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-full max-w-lg glass-cyber border-l border-[#00D9FF]/25 shadow-2xl p-5 overflow-y-auto animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-lg font-display font-bold uppercase tracking-wider neon-cyan">
            <ShieldCheck className="w-5 h-5" /> Admin · Users
          </h2>
          <button data-testid="close-admin-button" onClick={onClose} className="text-[#88A0C0] hover:text-[#00D9FF]"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2 h-10 bg-[#0F1636] border border-white/10 rounded-lg px-3 focus-within:border-[#00D9FF] mb-3">
          <Search className="w-4 h-4 text-[#71717A]" />
          <input
            data-testid="admin-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
            placeholder="Search by username or email…"
            className="flex-1 bg-transparent outline-none text-sm text-[#F1F1F5] placeholder:text-[#71717A]"
          />
        </div>
        <p className="text-[11px] text-[#88A0C0] mb-3">{total} total members</p>

        <div className="space-y-2" data-testid="admin-user-list">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 bg-white/5 rounded-lg p-2.5" data-testid={`admin-user-${u.username}`}>
              <span className="w-9 h-9 shrink-0 rounded-full bg-[#060817] border border-[#00D9FF]/30 flex items-center justify-center text-lg">{u.flag}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-[#F0F6FF] font-semibold truncate">@{u.username}</span>
                  {u.isAdmin && <Crown className="w-3 h-3 text-[#FFC107]" title="Admin" />}
                  {u.online && <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66]" style={{ boxShadow: "0 0 6px #00FF66" }} />}
                </div>
                <p className="text-[11px] text-[#88A0C0] truncate">{u.email} · {u.authProvider}</p>
              </div>
              <button
                data-testid={`admin-reset-${u.username}`}
                onClick={() => reset(u)}
                disabled={busyId === u.id}
                className="btn-neon shrink-0 flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#FF006E]/12 border border-[#FF006E]/50 text-[#FF006E] text-xs font-bold uppercase tracking-wide hover:bg-[#FF006E]/25 disabled:opacity-50"
              >
                <KeyRound className="w-3.5 h-3.5" /> Reset
              </button>
            </div>
          ))}
          {users.length === 0 && <p className="text-sm text-[#71717A] py-4 text-center">No users match “{q}”.</p>}
        </div>
      </div>
    </div>
  );
}
